import {
  AspectRatio,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import {
  GatsbyImage,
  IGatsbyImageData,
  StaticImage,
  getImage,
} from "gatsby-plugin-image";
/* eslint-disable no-unused-vars */
import React, { FC, useEffect, useMemo, useState } from "react";

import ADD_TO_CART from "../../types/AddToCard";
import { MdShoppingCart } from "../../customIcons";
import { calculateDiscountPercentage } from "../../utils/shopUtils";
import { useAppState } from "../../context";
import { useMutation } from "@apollo/client";
import { useNumberInput } from "@chakra-ui/react";

export enum WpStockStatusEnum {
  IN_STOCK = "IN_STOCK",
  OUT_OF_STOCK = "OUT_OF_STOCK",
  ON_BACKORDER = "ON_BACKORDER",
}

type ProductCardType = {
  marginBottom?: string | object;
  price?: string;
  salePrice?: string;
  name?: string;
  imageData: IGatsbyImageData;
  imageUrl?: string;
  productId?: number;
  slug?: string;
  description?: string;
  regularPrice?: string;
  width?: string | object;
  isSearch?: boolean;
  border?: string;
  height?: string | object;
  activeSubstance?: [];
  heading: any;
  isRelatedProducts?: boolean;
  stockStatus?: WpStockStatusEnum;
  tierPrice?: any;
  isProductOnPromotion?: boolean;
  hasAdditionalPromoTags?: string;
  disclaimer?: string;
  altText: string;
  isCart?: boolean;
  isVariable?: boolean;
};

const ProductCard: FC<ProductCardType> = ({
  marginBottom,
  name,
  price,
  salePrice,
  imageData,
  imageUrl,
  productId,
  slug,
  description,
  regularPrice,
  width,
  isSearch,
  border,
  height,
  activeSubstance,
  heading,
  isRelatedProducts,
  tierPrice,
  isProductOnPromotion,
  hasAdditionalPromoTags,
  altText,
  isCart,
  isVariable,
}) => {
  const toast = useToast();
  const { setCart } = useAppState();
  const image = imageData ? getImage(imageData) : null;

  const isDesktop = useBreakpointValue({ base: false, md: true });

  const size = useBreakpointValue({ base: "200px", md: "246px" });

  const formatPrice = (price: string | undefined): string => {
    if (!price) return "";
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numericPrice);
  };

  const tierPricesData = useMemo(() => {
    if (!tierPrice) {
      return null;
    }

    return [
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price,
        )}%`,
        price: tierPrice?.[0]?.price2,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price2,
        )}%`,
        price: tierPrice?.[0]?.price2,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price3,
        )}%`,
        price: tierPrice?.[0]?.price3,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price4,
        )}%`,
        price: tierPrice?.[0]?.price4,
      },
    ];
  }, [tierPrice]);

  const handleProductClick = () => {
    const productInfoForGA = {
      item_name: name,
      item_id: productId,
      price: price ? parseFloat(price?.replace("$", "")) : undefined,
      item_list_name: "Shop page",
      item_list_id: "shop_page",
      quantity: 1,
    };

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "select_item",
      ecommerce: {
        items: [productInfoForGA],
      },
    });
  };

  useEffect(() => {
    const productCardElement = document.getElementById(
      `product-card-${productId}`,
    );
    if (productCardElement) {
      productCardElement.addEventListener("click", handleProductClick);

      return () => {
        productCardElement.removeEventListener("click", handleProductClick);
      };
    }
  }, [productId]);

  const [addToCart, { loading }] = useMutation(ADD_TO_CART, {
    onCompleted: ({ addToCart }) => {
      toast({
        title: (
          <Text>
            Added to&nbsp;
            <Link
              href="https://www.medsupplysolutions.com/cart/"
              fontWeight={700}
              textDecoration="underline"
            >
              cart
            </Link>
          </Text>
        ),
        status: "success",
      });
      setCart(addToCart.cart);

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_name: name,
              item_id: productId,
              price: price ? parseFloat(price?.replace("$", "")) : undefined,
              quantity: "1",
            },
          ],
        },
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "There was an error adding your product",
        status: "error",
      });
    },
  });

  const [value, setValue] = useState(1);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: 1,
      min: 1,
      max: 999,
      precision: 0,
      value: value,
      onChange: (e: any) => setValue(e),
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  const {
    priceToShow,
    selectedAmountId,
  }: { priceToShow: string | undefined; selectedAmountId: number | undefined } =
    useMemo(() => {
      if (!tierPricesData) {
        return {
          priceToShow: formatPrice(regularPrice as string),
          selectedAmountId: undefined,
        };
      }

      if (value >= 21) {
        return {
          priceToShow: `${formatPrice(tierPricesData[3].price)}.00`,
          selectedAmountId: 3,
        };
      }

      if (value >= 11) {
        return {
          priceToShow: `${formatPrice(tierPricesData[2].price)}.00`,
          selectedAmountId: 2,
        };
      }

      if (value >= 3) {
        return {
          priceToShow: `${formatPrice(tierPricesData[1].price)}.00`,
          selectedAmountId: 1,
        };
      }

      if (value < 3) {
        return {
          priceToShow: formatPrice(regularPrice),
          selectedAmountId: undefined,
        };
      }

      return {
        priceToShow: formatPrice(regularPrice),
        selectedAmountId: undefined,
      };
    }, [value, tierPricesData, regularPrice]);

  const handleAmountClick = ({
    amountId,
    amountToSet,
  }: {
    amountId: number;
    amountToSet: number;
  }): void => {
    if (selectedAmountId === amountId) {
      setValue(1);
    } else {
      setValue(amountToSet);
    }
  };

  const handleAddToCart = () => {
    addToCart({
      variables: { input: { productId, quantity: Number(input.value) } },
    });
  };

  return (
    <Container
      padding="0px"
      bg="#fff"
      width={width}
      mb={marginBottom}
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.05)"
      border={border}
      borderRadius="8px"
      pt="6px"
      h={height}
      position="relative"
    >
      <div id={`product-card-${productId}`}>
        <Link
          href={`https://www.medsupplysolutions.com/product/${slug}/`}
          _hover={{ textDecoration: "none" }}
        >
          <Stack gap={0}>
            <Stack bg="#fff" borderRadius="8px" gap={0}>
              {isVariable && (
                <Box
                  position="relative"
                  top="4px"
                  left="4px"
                  zIndex="999"
                  p="8px"
                  bg="#C6F6D5"
                  color="#22543D"
                  borderRadius="full"
                  w="max-content"
                  fontSize="14px"
                  fontWeight="400"
                >
                  OUTLET
                </Box>
              )}

              <Box w={size} h={size} mx="auto">
                {isCart ? (
                  <AspectRatio
                    position="relative"
                    mx="auto"
                    width={{ base: "200px", md: "246px" }}
                    height={{ base: "200px", md: "246px" }}
                    ratio={1 / 1}
                  >
                    <Image
                      src={imageUrl}
                      alt={altText}
                      width="246px"
                      height="246px"
                    />
                  </AspectRatio>
                ) : (
                  <AspectRatio ratio={1 / 1}>
                    {image ? (
                      <GatsbyImage
                        image={image}
                        alt={altText || "Product image"}
                        style={{ height: "100%", width: "100%" }}
                      />
                    ) : (
                      <StaticImage
                        rel="preload"
                        src="../../../images/productplaceholder.png"
                        alt="Product placeholder"
                        style={{
                          height: "100%",
                          width: "100%",
                          backgroundColor: "transparent !important",
                        }}
                      />
                    )}
                  </AspectRatio>
                )}
              </Box>
              {isProductOnPromotion && (
                <Box
                  px="6px"
                  bg="#F0FFF4"
                  ml="12px"
                  position="absolute"
                  color="#39A26A"
                >
                  <Text fontSize="14px" fontWeight="600">
                    Limited Availability
                  </Text>
                </Box>
              )}
              {hasAdditionalPromoTags && (
                <Box
                  px="6px"
                  bg="#F0FFF4"
                  ml="12px"
                  position="absolute"
                  top="32px"
                  color="#39A26A"
                >
                  <Text fontSize="14px" fontWeight="600">
                    {hasAdditionalPromoTags}
                  </Text>
                </Box>
              )}
            </Stack>
            <Stack gap={0}>
              <Box p={3} h={isSearch ? "60px" : "70px"}>
                <Heading
                  as={heading}
                  noOfLines={2}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight={500}
                >
                  {name}
                </Heading>
              </Box>
              {activeSubstance && (
                <Box h={14} px={3} noOfLines={3}>
                  {activeSubstance?.map((tag: any) => (
                    <Text w="100%" color="#4A5568" fontSize="12px" key={tag.id}>
                      {tag.name}
                    </Text>
                  ))}
                </Box>
              )}
              {description && (
                <Box
                  px={3}
                  fontSize="sm"
                  color="#4A5568"
                  noOfLines={2}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {isVariable ? (
                <HStack pb="8px" px={3} h={10}>
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    _hover={{ textDecoation: "none" }}
                    color="#39A26A"
                  >
                    {price}
                  </Text>
                </HStack>
              ) : salePrice ? (
                <HStack pb="8px" px={3} h={10}>
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    _hover={{ textDecoation: "none" }}
                    color="#39A26A"
                  >
                    {`${formatPrice(salePrice)}`}
                  </Text>
                  <Text
                    fontSize="14px"
                    color="#4A5568"
                    textDecor="line-through"
                  >
                    {`${formatPrice(regularPrice)}`}
                  </Text>
                </HStack>
              ) : (
                <HStack pb="8px" px={3} h={10}>
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    _hover={{ textDecoation: "none" }}
                    color="#39A26A"
                  >
                    {priceToShow}
                  </Text>
                </HStack>
              )}
            </Stack>
          </Stack>
        </Link>
        {!isVariable && !isProductOnPromotion ? (
          tierPricesData && (
            <>
              <Text px={3} pb={2} color="#4A5568" fontWeight="600">
                Tier pricing
              </Text>
              <VStack gap={0} px={2} alignItems="center" w="100%">
                <HStack
                  pb={6}
                  w="100%"
                  justifyContent={{ base: "unset", md: "center" }}
                >
                  <VStack gap={0} w="100%">
                    <Text color="#BB5D82" fontSize="12px" fontWeight="600">
                      Save {tierPricesData[1]?.discount}
                    </Text>
                    <Box
                      cursor="pointer"
                      textAlign="center"
                      p={1}
                      minH={{ base: "78px", lg: "78px", xl: "auto" }}
                      w={{ base: "100%", md: "auto" }}
                      display="flex"
                      flexFlow="column"
                      alignItems="center"
                      justifyContent="center"
                      border={
                        selectedAmountId === 1
                          ? "1px solid #BB5D82"
                          : "1px solid #E1E7EF"
                      }
                      borderRadius="4px"
                      onClick={() =>
                        handleAmountClick({ amountId: 1, amountToSet: 3 })
                      }
                    >
                      <Text color="#4A5568" fontSize="14px" fontWeight="600">
                        3 or more
                      </Text>
                      <Text fontSize="12px" color="#4A5568">
                        {formatPrice(tierPricesData[1]?.price)} each
                      </Text>
                    </Box>
                  </VStack>
                  <VStack gap={0} w="100%">
                    <Text color="#BB5D82" fontSize="12px" fontWeight="600">
                      Save {tierPricesData[2]?.discount}
                    </Text>
                    <Box
                      cursor="pointer"
                      textAlign="center"
                      p={1}
                      minH={{ base: "78px", lg: "78px", xl: "auto" }}
                      w={{ base: "100%", md: "auto" }}
                      display="flex"
                      flexFlow="column"
                      alignItems="center"
                      justifyContent="center"
                      border={
                        selectedAmountId === 2
                          ? "1px solid #BB5D82"
                          : "1px solid #E1E7EF"
                      }
                      borderRadius="4px"
                      onClick={() =>
                        handleAmountClick({ amountId: 2, amountToSet: 11 })
                      }
                    >
                      <Text color="#4A5568" fontSize="14px" fontWeight="600">
                        11 or more
                      </Text>
                      <Text fontSize="12px" color="#4A5568">
                        {formatPrice(tierPricesData[2]?.price)} each
                      </Text>
                    </Box>
                  </VStack>
                  <VStack gap={0} w="100%">
                    <Text color="#BB5D82" fontSize="12px" fontWeight="600">
                      Save {tierPricesData[3]?.discount}
                    </Text>
                    <Box
                      cursor="pointer"
                      textAlign="center"
                      p={1}
                      minH={{ base: "78px", lg: "78px", xl: "auto" }}
                      w={{ base: "100%", md: "auto" }}
                      display="flex"
                      flexFlow="column"
                      alignItems="center"
                      justifyContent="center"
                      border={
                        selectedAmountId === 3
                          ? "1px solid #BB5D82"
                          : "1px solid #E1E7EF"
                      }
                      onClick={() =>
                        handleAmountClick({ amountId: 3, amountToSet: 21 })
                      }
                      borderRadius="4px"
                    >
                      <Text color="#4A5568" fontSize="14px" fontWeight="600">
                        21 or more
                      </Text>
                      <Text fontSize="12px" color="#4A5568">
                        {formatPrice(tierPricesData[3]?.price)} each
                      </Text>
                    </Box>
                  </VStack>
                </HStack>
              </VStack>
            </>
          )
        ) : (
          <Box h="120px" bg="white" px={3}>
            <Text pt="30px" color="4A5568">
              Promo and tiered pricing discounts cannot be combined
            </Text>
          </Box>
        )}
        {!isSearch && (
          <HStack
            mx="12px"
            pb="12px"
            justifyContent={{ base: "space-between", md: "unset" }}
          >
            <>
              {!isVariable ? (
                <HStack
                  gap="0"
                  w={{ base: "auto", md: "100%" }}
                  border="1px solid #CBD5E0"
                  borderRadius="6px"
                  h="40px"
                  p={0}
                  justifyContent="space-between"
                >
                  <Input
                    placeholder="-"
                    {...dec}
                    fontSize="2xl"
                    p={0}
                    w={10}
                    textAlign="center"
                    border="none"
                  />
                  <Input
                    maxLength={3}
                    focusBorderColor="transparent"
                    border="none"
                    p={0}
                    w={8}
                    {...input}
                    textAlign="center"
                  />
                  <Input
                    placeholder="+"
                    {...inc}
                    fontSize="2xl"
                    p={0}
                    w={10}
                    textAlign="center"
                    border="none"
                  />
                </HStack>
              ) : null}
              <Button
                borderRadius="6px"
                h={10}
                w="100%"
                justifyContent="center"
                color="#fff"
                background="#3B2060"
                _hover={{ background: "#6A3AAB", color: "#fff" }}
                fontSize="sm"
                fontWeight="600"
                leftIcon={
                  <MdShoppingCart width="16px" height="16px" fill="#fff" />
                }
                isLoading={loading}
                loadingText={
                  isDesktop ? "Add to cart" : <MdShoppingCart fill="#fff" />
                }
                onClick={
                  isVariable
                    ? () =>
                        (window.location.href = `https://www.medsupplysolutions.com/product/${slug}/`)
                    : handleAddToCart
                }
              >
                {isVariable ? (
                  "Choose"
                ) : isRelatedProducts || isDesktop ? (
                  "Add to cart"
                ) : (
                  <></>
                )}
              </Button>
            </>
          </HStack>
        )}
      </div>
    </Container>
  );
};

export default ProductCard;
